/**
 * Gallery V2 API
 *
 * API-Funktionen für die neue Bildergalerie.
 */

import { apiClient } from '../api-client';
import type { Image } from '@/types';

// =============================================================================
// Types
// =============================================================================

export interface GallerySearchParams {
  q?: string;
  tags?: string[];
  template_id?: string;
  ratio?: string;
  format?: string;
  created_by?: string;
  folder_id?: string;
  favorites_only?: boolean;
  date_from?: string;
  date_to?: string;
  approval_status?: Array<'pending' | 'approved' | 'rejected'>;
  sort_by?: 'created_at' | 'display_name' | 'self_rating';
  sort_order?: 'asc' | 'desc';
  min_rating?: number;
  page?: number;
  per_page?: number;
}

export interface TagPreview {
  id: string;
  name: string;
  color: string;
}

export interface FolderPreview {
  id: string;
  name: string;
}

export interface CreatorInfo {
  id: string;
  username: string | null;
  email: string;
}

export interface GalleryImage {
  id: string;
  display_name: string | null;
  alt_title: string | null;
  prompt: string;
  ratio: string | null;
  file_format: string | null;
  image_url: string | null;
  s3_key: string | null;
  template_id: string | null;
  template_name: string | null;
  created_by: CreatorInfo;
  tag_count: number;
  tags: TagPreview[];
  folder_count: number;
  folders: FolderPreview[];
  is_favorite: boolean;
  is_public_image: boolean;
  public_url: string | null;
  created_at: string;
  approval_status?: 'pending' | 'approved' | 'rejected';
  // Rating fields
  average_rating?: number;
  rating_count?: number;
  user_rating?: number | null;
}

export interface GalleryPagination {
  page: number;
  per_page: number;
  total_pages: number;
  total_images: number;
}

export interface GallerySearchResponse {
  images: GalleryImage[];
  pagination: GalleryPagination;
  filters_applied: Record<string, unknown>;
}

export interface FilterOptions {
  users: Array<{ id: string; username: string | null; email: string }>;
  templates: Array<{ id: string; name: string }>;
  ratios: string[];
  formats: string[];
}

export interface FolderNode {
  id: string;
  name: string;
  slug: string;
  depth: number;
  color: string;
  icon: string;
  image_count: number;
  children: FolderNode[];
}

export interface SavedSearch {
  id: string;
  name: string;
  filters: Record<string, unknown>;
  is_shared: boolean;
  usage_count: number;
  created_at: string;
}

// =============================================================================
// Gallery Search API
// =============================================================================

export async function searchGallery(
  params: GallerySearchParams
): Promise<GallerySearchResponse> {
  const searchParams = new URLSearchParams();

  if (params.q) searchParams.set('q', params.q);
  if (params.tags?.length) searchParams.set('tags', params.tags.join(','));
  if (params.template_id) searchParams.set('template_id', params.template_id);
  if (params.ratio) searchParams.set('ratio', params.ratio);
  if (params.format) searchParams.set('format', params.format);
  if (params.created_by) searchParams.set('created_by', params.created_by);
  if (params.folder_id) searchParams.set('folder_id', params.folder_id);
  if (params.favorites_only) searchParams.set('favorites_only', 'true');
  if (params.date_from) searchParams.set('date_from', params.date_from);
  if (params.date_to) searchParams.set('date_to', params.date_to);
  if (params.approval_status && params.approval_status.length > 0) {
    searchParams.set('approval_status', params.approval_status.join(','));
  }
  if (params.min_rating !== undefined) searchParams.set('min_rating', params.min_rating.toString());
  if (params.sort_by) searchParams.set('sort_by', params.sort_by);
  if (params.sort_order) searchParams.set('sort_order', params.sort_order);
  if (params.page) searchParams.set('page', params.page.toString());
  if (params.per_page) searchParams.set('per_page', params.per_page.toString());

  const response = await apiClient.get(
    `/api/v2/tools/gallery/search?${searchParams.toString()}`
  );
  return response.data;
}

export async function getFilterOptions(): Promise<FilterOptions> {
  const response = await apiClient.get('/api/v2/tools/gallery/filter-options');
  return response.data;
}

export async function updateImageMetadata(
  imageId: string,
  data: { display_name?: string; alt_title?: string }
): Promise<void> {
  await apiClient.patch(`/api/v2/tools/gallery/images/${imageId}`, data);
}

// =============================================================================
// Folders API
// =============================================================================

export async function getFolders(): Promise<{ folders: FolderNode[] }> {
  const response = await apiClient.get('/api/v2/tools/gallery/folders');
  return response.data;
}

export async function createFolder(data: {
  name: string;
  parent_id?: string;
  description?: string;
  color?: string;
  icon?: string;
}): Promise<{ folder: FolderNode }> {
  const response = await apiClient.post('/api/v2/tools/gallery/folders', data);
  return response.data;
}

export async function updateFolder(
  folderId: string,
  data: {
    name?: string;
    description?: string;
    color?: string;
    icon?: string;
  }
): Promise<{ folder: FolderNode }> {
  const response = await apiClient.patch(
    `/api/v2/tools/gallery/folders/${folderId}`,
    data
  );
  return response.data;
}

export async function deleteFolder(folderId: string): Promise<void> {
  await apiClient.delete(`/api/v2/tools/gallery/folders/${folderId}`);
}

export async function addImagesToFolder(
  folderId: string,
  imageIds: string[]
): Promise<{ added: number; skipped: number }> {
  const response = await apiClient.post(
    `/api/v2/tools/gallery/folders/${folderId}/images`,
    { image_ids: imageIds }
  );
  return response.data;
}

export async function removeImageFromFolder(
  folderId: string,
  imageId: string
): Promise<void> {
  await apiClient.delete(
    `/api/v2/tools/gallery/folders/${folderId}/images/${imageId}`
  );
}

export async function moveFolder(
  folderId: string,
  data: { parent_id: string | null; sort_order: number }
): Promise<{ folder: FolderNode }> {
  const response = await apiClient.post(
    `/api/v2/tools/gallery/folders/${folderId}/move`,
    data
  );
  return response.data;
}

export async function reorderFolders(
  folders: Array<{ id: string; sort_order: number }>
): Promise<void> {
  await apiClient.post('/api/v2/tools/gallery/folders/reorder', { folders });
}

export async function toggleFolderFavorite(
  folderId: string
): Promise<{ is_favorite: boolean; action: 'added' | 'removed' }> {
  const response = await apiClient.post(
    `/api/v2/tools/gallery/folders/${folderId}/favorite/toggle`
  );
  return response.data;
}

export async function getFolderFavorites(): Promise<{ favorite_ids: string[] }> {
  const response = await apiClient.get('/api/v2/tools/gallery/folders/favorites');
  return response.data;
}

// =============================================================================
// Favorites API
// =============================================================================

export async function toggleFavorite(
  imageId: string
): Promise<{ is_favorite: boolean; action: 'added' | 'removed' }> {
  const response = await apiClient.post(
    `/api/v2/tools/gallery/images/${imageId}/favorite/toggle`
  );
  return response.data;
}

export async function addFavorite(imageId: string): Promise<void> {
  await apiClient.post(`/api/v2/tools/gallery/images/${imageId}/favorite`);
}

export async function removeFavorite(imageId: string): Promise<void> {
  await apiClient.delete(`/api/v2/tools/gallery/images/${imageId}/favorite`);
}

// =============================================================================
// Saved Searches API
// =============================================================================

export async function getSavedSearches(): Promise<{
  saved_searches: SavedSearch[];
}> {
  const response = await apiClient.get('/api/v2/tools/gallery/saved-searches');
  return response.data;
}

export async function createSavedSearch(data: {
  name: string;
  filters: Record<string, unknown>;
  is_shared?: boolean;
}): Promise<{ saved_search: SavedSearch }> {
  const response = await apiClient.post(
    '/api/v2/tools/gallery/saved-searches',
    data
  );
  return response.data;
}

export async function deleteSavedSearch(searchId: string): Promise<void> {
  await apiClient.delete(`/api/v2/tools/gallery/saved-searches/${searchId}`);
}

// =============================================================================
// Bulk Download API
// =============================================================================

export async function bulkDownload(imageIds: string[]): Promise<Blob> {
  const response = await apiClient.post(
    '/api/v2/tools/gallery/bulk/download',
    { image_ids: imageIds },
    { responseType: 'blob' }
  );
  return response.data;
}

// =============================================================================
// Approval API
// =============================================================================

export interface ApprovalStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  approval_rate: number;
  pending_percentage: number;
}

export interface SmartModeRecommendation {
  recommended_mode: 'single' | 'multi';
  pending_count: number;
  reason: string;
}

export async function getApprovalStats(): Promise<ApprovalStats> {
  const response = await apiClient.get('/api/v2/tools/gallery/approval/stats');
  return response.data;
}

export async function updateApprovalStatus(
  imageId: string,
  action: 'approve' | 'reject' | 'skip' | 'reset',
  notes?: string
): Promise<{ success: boolean; image: GalleryImage }> {
  const response = await apiClient.patch(
    `/api/v2/tools/gallery/approval/images/${imageId}`,
    { action, notes }
  );
  return response.data;
}

export async function bulkUpdateApproval(
  imageIds: string[],
  action: 'approve' | 'reject',
  notes?: string
): Promise<{ success: boolean; success_count: number; failed_count: number; total: number }> {
  const response = await apiClient.post(
    '/api/v2/tools/gallery/approval/images/bulk',
    { image_ids: imageIds, action, notes }
  );
  return response.data;
}

export async function getSmartModeRecommendation(): Promise<SmartModeRecommendation> {
  const response = await apiClient.get('/api/v2/tools/gallery/approval/smart-mode');
  return response.data;
}

// =============================================================================
// Mapper: GalleryImage → Legacy Image Type
// =============================================================================

/**
 * Konvertiert GalleryImage zu Legacy Image Format.
 * Ermöglicht Nutzung der neuen API mit bestehenden Komponenten.
 */
export function galleryImageToLegacy(img: GalleryImage): Image {
  return {
    id: img.id,
    user_id: img.created_by.id,
    workspace_id: null, // Nicht in GalleryImage enthalten
    prompt: img.prompt,
    ratio: img.ratio || '1:1',
    preset_type: img.template_name || 'default',
    image_url: img.image_url || '',
    s3_key: img.s3_key,
    is_public_image: img.is_public_image,
    public_url: img.public_url,
    made_public_at: null, // Nicht in GalleryImage enthalten
    created_at: img.created_at,
    duration: null, // Nicht in GalleryImage enthalten
    template_id: img.template_id,
    template_name: img.template_name,
    approval_status: img.approval_status,
    // Asset-related fields for product assignment
    asset_id: img.id, // Use image id - backend can resolve to asset
    width: undefined,
    height: undefined,
    // Rating fields
    average_rating: img.average_rating,
    rating_count: img.rating_count,
    user_rating: img.user_rating,
  };
}

/**
 * Konvertiert Array von GalleryImages zu Legacy Format.
 */
export function galleryImagesToLegacy(images: GalleryImage[]): Image[] {
  return images.map(galleryImageToLegacy);
}

// =============================================================================
// Gallery Settings API
// =============================================================================

export type DefaultApprovalFilter = 'pending_approved' | 'approved_only';

export interface GallerySettings {
  approval_mode_enabled: boolean;
  default_approval_filter: DefaultApprovalFilter;
}

export async function getGallerySettings(): Promise<GallerySettings> {
  const response = await apiClient.get('/api/v2/tools/gallery/settings');
  return response.data;
}

export async function updateGallerySettings(
  settings: Partial<GallerySettings>
): Promise<GallerySettings> {
  const response = await apiClient.patch('/api/v2/tools/gallery/settings', settings);
  return response.data;
}
